import { v4 as uuid } from 'uuid';
import { APP_CALLS } from '$lib/utils/constants'; // Adjust the path as necessary
import { Sentry } from '$lib/services/sentry';

export { AppService };

// Utility function to parse URLs
const parseURL = (url = '') => {
	const httpRegex = /(https?):\/\/(.*)/;
	const matches = url.match(httpRegex);
	const out = {
		schema: '',
		href: ''
	};

	if (matches !== null) {
		out.schema = matches[1];
		out.href = matches[2];
	}

	return out;
};

class AppService {
	constructor() {
		this.storage = {};
		this.responseHandler = this.responseHandler.bind(this);
	}

	/**
	 * @param {{ data: any; }} event
	 */
	responseHandler(event) {
		console.debug('DEBUG', 'HANDLE_RESPONSE', JSON.stringify(event?.data, null, 2));
		if (typeof event !== 'object' || typeof event.data === 'undefined') {
			return;
		}
		const { data } = event;
		const callbacks = this.getAllCallbacks();

		for (const key in callbacks) {
			if (callbacks.hasOwnProperty(key)) {
				callbacks[key](data);
			}
		}
	}

	prepareQueryString(_params) {
		if (typeof _params !== 'object') {
			throw new Error('invalid params');
		}

		const params = [];
		for (const key in _params) {
			if (_params.hasOwnProperty(key)) {
				params.push(`${key}=${encodeURIComponent(_params[key])}`);
			}
		}
		return params.join('&');
	}

	/**
	 * @param {Location | (string)} _uri - the message uri to send.
	 * @returns {void}
	 */
	makeCall(_uri) {
		// posting a message to the window for automated testing purposes only
		window.postMessage({
			type: 'app-call',
			payload: _uri
		}, '*');
		
		if (typeof cefAOFL !== 'undefined' && typeof cefAOFL.UnityMessage !== 'undefined') {
			cefAOFL.UnityMessage(_uri);
		} else {
			// TODO: change this to href.
			window.location = _uri;
		}
	}

	/**
	 * @param {string} appCall - the app call
	 * @param {object} payload - the payload for the appcall
	 * @param {string} breadcrumbMessage - message for breadcrumb
	 * @returns {Promise<string>}
	 */
	async send(appCall, payload = {}, breadcrumbMessage) {
		console.debug('DEBUG: ', 'SEND', appCall, JSON.stringify(payload, null, 2));
		return await new Promise((resolve, _reject) => {
			const requestId = uuid();

			Sentry.addBreadcrumb({
				category: 'app-call',
				message: appCall,
				data: {
					requestId,
					breadcrumbMessage,
					...payload
				},
				level: 'info'
			});

			const callback = (/** @type {{ request: any; payload: any; }} */ data) => {
				console.debug(
					'DEBUG: ',
					`RECEIEVED for ${JSON.stringify(payload)}`,
					JSON.stringify(data, null, 2)
				);
				if (
					typeof data !== 'object' ||
					typeof data.request === 'undefined' ||
					data.request !== payload.request
				) {
					console.debug('Skipping call', data);
					return;
					// TODO: doing the above to follow old sys. design
					// this.removeCallback(requestId);
					// return reject(data);
				}

				this.removeCallback(requestId);
				return resolve(data.payload);
			};

			this.setCallback(requestId, callback);

			if (this.getCallbacksSize() === 1) {
				window.addEventListener('message', this.responseHandler);
			}

			let uri = this.constructUri(appCall, payload);
			this.makeCall(uri);
		});
	}

	/**
	 * @param {string} appCall - the app call to request
	 * @param {object} payload - the payload for request
	 * @returns {string}
	 */
	constructUri(appCall, payload) {
		let uri = appCall;
		const queryString = this.prepareQueryString(payload);
		if (queryString.length) {
			uri += '?' + queryString;
		}
		return uri;
	}

	/**
	 * Sets a callback function for a given key.
	 * @param {string} key - The key to associate with the callback.
	 * @param {(data: any) => void} callback - The callback function to be set.
	 * @returns {void}
	 */
	setCallback(key, callback) {
		return (this.storage[key] = callback);
	}

	getCallback(key) {
		return this.storage[key];
	}

	removeCallback(key) {
		delete this.storage[key];
	}

	getAllCallbacks() {
		return this.storage;
	}

	getCallbacksSize() {
		return Object.keys(this.storage).length;
	}

	/**
	 * @param {{ href?: string; target?: string; showParentGate?: boolean; }} params - parameters
	 * @returns {Promise<void>}
	 */
	async navigate(params) {
		const { target, showParentGate } = params;

		return await this.send(
			APP_CALLS.NAVIGATE,
			{
				...parseURL(params.href),
				target,
				showParentGate
			},
			'navigate'
		);
	}

	/**
	 * Logs in a user with the provided payload.
	 * @param {object} payload - The payload containing login information.
	 * @param {string} payload.token - The authentication token.
	 * @param {string} payload.userUuid - The unique identifier of the user.
	 * @param {string} payload.userAccountType - The type of user account.
	 * @param {any} payload.request - The request object or data. Adjust this type if the shape is known.
	 * @returns {Promise<any>} - A promise that resolves with the result of the login attempt. Adjust the type if the return type is known.
	 */
	async login(payload) {
		const params = {
			token: payload.token,
			user_uuid: payload.userUuid,
			user_account_type: payload.userAccountType,
			request: payload.request
		};

		return await this.send(APP_CALLS.LOGIN, params, 'login');
	}

	async purchase(params) {
		console.debug('DEBUG: ', 'PURCHASE', JSON.stringify(params, null, 2));
		return await this.send(APP_CALLS.PURCHASE, params, 'purchase');
	}

	async upgrade(params) {
		console.debug('DEBUG: ', 'UPGRADE', JSON.stringify(params, null, 2));
		return await this.send(APP_CALLS.UPGRADE, params, 'upgrade');
	}

	async restoreTransaction(params) {
		console.debug('DEBUG: ', 'RESTOR_TX', JSON.stringify(params, null, 2));
		return await this.send(APP_CALLS.RESTORE, params, 'restoreTransaction');
	}

	async logEvent(payload) {
		console.debug('DEBUG: ', 'LOG_EV', JSON.stringify(payload, null, 2));
		return await this.send(APP_CALLS.LOG_EVENT, payload, 'logEvent');
	}

	async logAdjustEvent(payload) {
		console.debug('DEBUG: ', 'ADJUST_LOG_EV', JSON.stringify(payload, null, 2));
		return await this.send(APP_CALLS.ADJUST_EVENT, payload, 'logEvent');
	}

	async logout() {
		console.debug('DEBUG: ', 'LOGOUT');
		return await this.send(APP_CALLS.LOGOUT, {}, 'logout');
	}

	async closeApp() {
		console.debug('DEBUG: ', 'CLOSE');
		return await this.send(APP_CALLS.CLOSE_APP, {}, 'closeApp');
	}

	async goBack() {
		console.debug('DEBUG: ', 'GO_BACK');
		return await this.send(APP_CALLS.GO_BACK, {}, 'goBack');
	}
}
